import { inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';

import { SegmentedZonesRepository } from '@data/repositories';
import { SegmentedZoneVariable } from '../../data/entities';
import { switchList } from '@shared/utils';
import { FetchingOptions } from '../../data/constants/fetching-options';
import { TFiltersPercentages } from '@domain/types';

type TClientSegmentationState = {
  fetchStack: string[];
  segmentedZones: SegmentedZoneVariable[];
};

const initialState: TClientSegmentationState = {
  fetchStack: [],
  segmentedZones: [],
};

@Injectable({
  providedIn: 'root',
})
export class ClientSegmentationProvider {
  readonly #state = signalState(initialState);
  readonly #segmentedZoneRepository = inject(SegmentedZonesRepository);

  readonly segmentedZones = this.#state.segmentedZones;
  readonly fetchStack = this.#state.fetchStack;

  async listSegmentedVariables({ filialId, operationId }: TFiltersPercentages) {
    patchState(this.#state, ({ fetchStack }) => ({
      fetchStack: switchList(fetchStack).add(
        FetchingOptions.ON_LIST_SEGMENTED_ZONES,
      ),
    }));

    const result = await this.#segmentedZoneRepository.listSegmentedVariables(
      filialId,
      operationId,
    );

    patchState(this.#state, ({ fetchStack }) => ({
      fetchStack: switchList(fetchStack).remove(
        FetchingOptions.ON_LIST_SEGMENTED_ZONES,
      ),
    }));

    if (result.isOk === false) {
      throw result.error;
    }

    patchState(this.#state, {
      segmentedZones: result.data,
    });

    return 'Success';
  }

  async updateClientSegmentedVariables(items: SegmentedZoneVariable[]) {
    patchState(this.#state, ({ fetchStack }) => ({
      fetchStack: switchList(fetchStack).add(
        FetchingOptions.ON_UPDATE_SEGMENTED_ZONES,
      ),
    }));

    const data = items.map((i) => i.toJson());

    const result =
      await this.#segmentedZoneRepository.updateClientSegmentedVariables(data);

    patchState(this.#state, ({ fetchStack }) => ({
      fetchStack: switchList(fetchStack).remove(
        FetchingOptions.ON_UPDATE_SEGMENTED_ZONES,
      ),
    }));

    if (result.isOk === false) {
      throw result.error;
    }

    return result.data;
  }
}
